/**
 * Burn rate for Luxury Escapes loyalty points
 * 1 point = $0.004 AUD, or equivalently $1 = 250 points
 */
// [TODO]: Get this from the backend
// https://aussiecommerce.atlassian.net/browse/SHOP-551
export const POINTS_TO_DOLLARS_RATE = 0.004

/**
 * Converts a price in dollars to the equivalent number of loyalty points
 * @param price The price in dollars
 * @returns The equivalent number of points (rounded to nearest whole number)
 */
export function convertPriceToPoints(price: number): number {
  return Math.round(price / POINTS_TO_DOLLARS_RATE)
}

/**
 * Converts loyalty points to the equivalent price in dollars
 * @param points The number of loyalty points
 * @returns The equivalent price in dollars
 */
export function convertPointsToPrice(points: number): number {
  return points * POINTS_TO_DOLLARS_RATE
}

export const MERCHANDISE_ORDER_DISCLAIMER = 'Your account will be credited within 30-40 days.'

export const MERCHANDISE_SHIPPING_DISCLAIMER = 'Some items may be shipped separately or together. Tracking links will appear once available.'

/**
 * Constants for merchandise variant option types
 */
export const MERCHANDISE_OPTION_TYPES = {
  COLOR: ['Color', 'Colour', 'Band colour'],
  SECONDARY_COLOR: ['Case color', 'Dial colour'],
  SIZE: ['Size', 'Memory Size', 'Bedding size'],
  SECONDARY_SIZE: ['Case size'],
}

/**
 * Color mapping for merchandise variants
 * Maps color names to their corresponding hex color codes
 */
export const MERCHANDISE_COLOR_MAP = new Map<string, string>([
  ['Almond Cream', '#F4E3C1'],
  ['Alpine Sage', '#9CAF88'],
  ['Aluminium', '#A7B0B7'],
  ['Anchor Blue', '#294C60'],
  ['Angora', '#E6DAD0'],
  ['Arctic Blue', '#C8E1F2'],
  ['Awesome Black', '#000000'],
  ['Awesome Graphite', '#41424C'],
  ['Awesome Lavender', '#E6E6FA'],
  ['Awesome Olive', '#6B6B47'],
  ['Beige', '#FFEFD4'],
  ['Beet Juice/Copper Rose Aluminium', '#A8626D'],
  ['Berry Red', '#B73247'],
  ['Birdee Colour Mix', '#7B9CB0'],
  ['Black', '#000000'],
  ['Black Anthracite', '#36454F'],
  ['Black/Graphite', '#2F2F2F'],
  ['Black Oak', '#2B2B2B'],
  ['Blue', '#8BC0FF'],
  ['Blue Black', '#1B1B3A'],
  ['Blue Dusk', '#4B5D7E'],
  ['Blue Mist/Pale Gold', '#D4E4F0'],
  ['Blue Salt', '#9FBED3'],
  ['Blue Shadow', '#6A8CAF'],
  ['Bonfire Orange', '#CC6633'],
  ['Bright Blue', '#0074E8'],
  ['Bronze', '#AD7E5F'],
  ['Brown', '#352319'],
  ['Butter', '#FFF1A8'],
  ['Cardamom', '#B7A57A'],
  ['Cast Iron Black', '#2B2B2B'],
  ['Champagne', '#F7E3BE'],
  ['Charcoal', '#3E3E3E'],
  ['Charcoal Grey', '#4A4A48'],
  ['Chestnut', '#8B4B3B'],
  ['Chili Red', '#C33A3A'],
  ['Chocolate', '#5E2C0C'],
  ['Chrome', '#B4B4B4'],
  ['Clay', '#C18F65'],
  ['Clear', '#F2E9E9'],
  ['Climbing Ivy', '#3C6240'],
  ['Cloud White', '#F7F7F7'],
  ['Cobalt Blue', '#3366CC'],
  ['Cobblestone', '#B2B0AA'],
  ['Cognac', '#9C5642'],
  ['Confetti Grey', '#B8B8B8'],
  ['Confetti Powder', '#E6E1E1'],
  ['Confetti Sand', '#D9CFC0'],
  ['Contour Silver', '#C9C7C7'],
  ['Copper', '#B87333'],
  ['Coral', '#F88379'],
  ['Coral Pink', '#F5A89E'],
  ['Coral Red', '#FF7F50'],
  ['Cosmic Orange', '#FF4500'],
  ['Cotton White', '#FEFEFB'],
  ['Cream', '#FDF3D6'],
  ['Croco Green', '#4A5D4F'],
  ['Dark Oak', '#5D4037'],
  ['Deep Blue', '#003366'],
  ['Deep Ocean', '#1E3F66'],
  ['Deep Sea', '#1B4B5A'],
  ['Deer Friends Colour Mix', '#A89B8E'],
  ['Deer Friends Grey', '#8B8680'],
  ['Dove', '#E8E8E8'],
  ['Dried Rose', '#C57B87'],
  ['Elphee Grey', '#9B9B9B'],
  ['Empire Red', '#A10D2F'],
  ['Enchanting Lilac', '#C5A8E2'],
  ['Eucalyptus Green', '#7A9B8E'],
  ['Feather Pink', '#F8DADF'],
  ['Field Green', '#5A7C54'],
  ['Flame', '#E36414'],
  ['Forest Green', '#2D5016'],
  ['Gold', '#D4AF37'],
  ['Gold Tone', '#C9A961'],
  ['Gorse Yellow', '#FFF14F'],
  ['Granite', '#666666'],
  ['Graphite', '#383838'],
  ['Gray', '#505460'],
  ['Green', '#3FA34D'],
  ['Grey', '#A0A0A0'],
  ['Grey Mist', '#C2C2C2'],
  ['Gun Metal', '#2F3B45'],
  ['Hazel', '#8E735B'],
  ['Hazy Lilac', '#D3C5D9'],
  ['High', ''],
  ['Honey Tone', '#D4A574'],
  ['Hourglass Sand', '#C4B5A0'],
  ['Hunter Green', '#305E44'],
  ['Hyper Pink', '#FF66A1'],
  ['Indigo Blue', '#4250A4'],
  ['Infinite Black', '#0F0F0F'],
  ['Iris', '#5A4FCF'],
  ['Ivory', '#FFF8E7'],
  ['Jet Black', '#0A0A0A'],
  ['Joyous Bright', '#FFC65C'],
  ['Juniper', '#507F70'],
  ['Khaki', '#D8CBA7'],
  ['Lalee Colour Mix', '#C4A68A'],
  ['Lavender', '#E6E6FA'],
  ['Light Blue', '#A9D3FF'],
  ['Light Blush', '#FADADD'],
  ['Light Gold', '#F0E68C'],
  ['Lilac', '#D6B4E1'],
  ['Lilac/Lime', '#C6A8E2'],
  ['Liquorice Blue', '#1E3A5F'],
  ['Lunar White/Platinum Aluminium', '#E8E8EA'],
  ['Matt Chrome', '#B0B0B0'],
  ['Matte Black', '#1C1C1C'],
  ['Mediterranean Blue', '#0067A5'],
  ['Midnight', '#191970'],
  ['Midnight Navy', '#121C3A'],
  ['Mint', '#98FB98'],
  ['Mirror Chrome', '#C0C0C0'],
  ['Mist Blue', '#C4E4FF'],
  ['Moonglow White', '#F5F3EE'],
  ['Moorland Pink', '#E5B7BF'],
  ['Multicolor', ''],
  ['Mustard', '#D4AC2B'],
  ['Natural', '#DDD2B4'],
  ['Natural Aluminium', '#A6A6A6'],
  ['Navy', '#1F3579'],
  ['Neon', '#39FF8A'],
  ['Nordic Weave', '#A8A8A8'],
  ['Oak', '#A0886B'],
  ['Obsidian', '#0B0C10'],
  ['Ocean Blue', '#006BA6'],
  ['Ochre', '#CC8B3B'],
  ['Off-White', '#FAF8F4'],
  ['Olive', '#7C7B4A'],
  ['Onyx Black', '#353839'],
  ['Orange', '#E9AE6C '],
  ['Pastel Blue', '#BFD8E6'],
  ['Pastel Green', '#CBE5C0'],
  ['Pastel Pink', '#F6C6CE'],
  ['Pearl/Fuschia', '#E7B6D3'],
  ['Peach', '#FFD5B8'],
  ['Pebble', '#A8A5A2'],
  ['Pebbled Palm', '#647D64'],
  ['Peony', '#FADADD'],
  ['Pink', '#F97FB4'],
  ['Pink Gold', '#E6B7A9'],
  ['Pink Sand/Copper Rose Aluminium', ''],
  ['Pistachio', '#93C572'],
  ['Platinum Grey', '#D9D9D9'],
  ['Porcelain', '#F5F5F3'],
  ['Powder', '#F0F0F0'],
  ['Porcelain White', '#F8F7F3'],
  ['Purple', '#8685E0'],
  ['Purple Fog', '#B9A0C9'],
  ['Quartz', '#CAB9C8'],
  ['Raffi Colour Mix', '#B8A99A'],
  ['Raffi Sand', '#D4C4B0'],
  ['Raspberry', '#E2507B'],
  ['Red', '#D43044 '],
  ['Rose gold', '#F48189'],
  ['Rose Quartz', '#F7CAC9'],
  ['Sage', '#9CAF88'],
  ['Sand', '#C7B299'],
  ['Sea Green', '#4A9B8E'],
  ['Sandstone', '#D2B48C'],
  ['Satin Chrome', '#D1D1D1'],
  ['Silver', '#C0C0C0'],
  ['Silver Mist', '#BDBDBD'],
  ['Silver Shadow', '#A8A8A8'],
  ['Sky Blue', '#87CEEB'],
  ['Slate', '#708090'],
  ['Smoke', '#848484'],
  ['Snow', '#FAFAFA'],
  ['Space Black', '#1C1C1E'],
  ['Spaced Aluminium', '#9E9E9E'],
  ['Space Gray', '#5C5C60'],
  ['Stainless Steel', '#D5D7DC'],
  ['Starlight', '#E3CBA8'],
  ['Stone Grey', '#7D7D7D'],
  ['Taupe', '#7E6C5B'],
  ['Teal', '#008080'],
  ['Timber', '#8B6F47'],
  ['Titanium Black', '#2E2E30'],
  ['Titanium Blue', '#6A8CAF'],
  ['Titanium Grey', '#878787'],
  ['Titanium Silver', '#BEC0C2'],
  ['Turquoise', '#4DD0E1'],
  ['Twilight Blue', '#1E385B'],
  ['Ultramarine', '#4166F5'],
  ['Wally Blue', '#5B7C99'],
  ['Waterfall Blue/Platinum Aluminium', ''],
  ['White', '#FFFFFF'],
  ['White Smoke', '#F5F5F5'],
  ['Wine', '#722F37'],
  ['Wintergreen', '#3CB371'],
  ['Yellow', '#FDDA0D'],
])
